import { useCallback, useEffect } from 'react';
import { FixedSizeList } from 'react-window';
import { useDropdownPosition } from '../../hooks';
import { ISelectValue } from '../../select.types';
import { useSelectContext } from '../../context';
import { SELECT_TEXTS } from '../../select.constants';
import { Option } from './components';
import { NoOptionsMessage, Wrapper } from './select-dropdown.styled';

// Константы высоты
const ITEM_HEIGHT = 40;
const MAX_HEIGHT = 380;
const MIN_HEIGHT = 40;

export const SelectDropdown = () => {
  const { state, refs, actions } = useSelectContext();

  // Вычисление высоты списка
  const listHeight = state.filteredOptions.length
    ? Math.min(
        Math.max(state.filteredOptions.length * ITEM_HEIGHT, MIN_HEIGHT),
        MAX_HEIGHT
      )
    : MIN_HEIGHT;

  const dropdownPositions = useDropdownPosition(
    {
      isOpen: state.isOpen,
      triggerRef: refs.triggerRef,
      contentRef: refs.dropdownRef,
      dropdownHeight: listHeight,
      dynamic: !state.closeOnScroll,
    },
    [state.filteredOptions.length, state.selectedOptions.length]
  );

  const handleCloseOnScroll = useCallback((e: Event) => {
    const target = e.target as HTMLElement;

    if (
      refs.dropdownRef.current?.contains(target) ||
      refs.searchRef.current?.contains(target)
    ) {
      return;
    }

    actions.closeDropdown();
    refs.searchRef.current?.blur();
  }, []);

  useEffect(() => {
    if (!state.closeOnScroll) {
      return;
    }

    const abortController = new AbortController();
    const { signal } = abortController;

    document.addEventListener('scroll', handleCloseOnScroll, {
      capture: true,
      signal,
    });
    window.addEventListener('resize', () => actions.closeDropdown(), {
      signal,
    });

    return () => {
      abortController.abort();
    };
  }, [state.closeOnScroll, handleCloseOnScroll]);

  const renderRow = ({
    data,
    index,
    style,
  }: {
    data: ISelectValue[];
    index: number;
    style: React.CSSProperties;
  }) => {
    const isSelected = state.multi
      ? state.value.includes(data[index].value)
      : state.value === data[index].value;

    return (
      <div style={style}>
        <Option
          option={data[index]}
          optionIndex={index}
          isSelected={isSelected}
        />
      </div>
    );
  };

  return (
    <Wrapper
      ref={refs.dropdownRef}
      style={dropdownPositions ?? {}}
      className="custom-select-dropdown"
    >
      {!!state.filteredOptions.length ? (
        <FixedSizeList
          ref={refs.listRef}
          width={Math.max(dropdownPositions?.width ?? 0, 280)}
          height={dropdownPositions?.maxHeight ?? listHeight}
          itemCount={state.filteredOptions.length}
          itemSize={ITEM_HEIGHT}
          itemData={state.filteredOptions}
          outerElementType={'div'}
        >
          {renderRow}
        </FixedSizeList>
      ) : (
        <NoOptionsMessage>
          {state.noOptionsMessage ??
            (!!state.search.length
              ? SELECT_TEXTS.noSearchResults
              : SELECT_TEXTS.noOptions)}
        </NoOptionsMessage>
      )}
    </Wrapper>
  );
};
